<script lang="ts">
	import { format } from 'svelte-i18n';
	import { version } from '../../package.json';
	import './classic-main-menu-rail.css';
	// Svelte hooks
	import { beforeUpdate, onDestroy, onMount } from 'svelte';
	// Services
	import { backForwardNavigation } from '../services/native_container_requests_ios';
	// Stores
	import { user_data, profile_data } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import { app, os, ios_version, android_version } from 'src/stores/session/native-bridge';
	import { is_mobile } from 'src/stores/ui/viewport';
	import { showBleControlSetupModal } from 'src/stores/features/ble/control-device';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { dashboardTabs } from 'src/stores/features/dashboards';
	import { deviceMainFilter, resetFiltersAllDevicesList } from 'src/stores/ui/filters';
	import { authLockedDeviceModal } from 'src/stores/device-runtime/firmware-ui';
	// Helpers
	import { trimLastChar } from '../helpers/utilities';
	import { getAppBackgroundSrc } from '../helpers/account_helpers';
	import {
		MAIN_MENU_ORDER_INDEX_ADD_DEVICE,
		MAIN_MENU_ORDER_INDEX_ASSISTANT,
		MENU_ITEMS_IDS,
		appendMissingMenuOrderIndices,
		getHeaderMenuItemDef,
		getLandingHref,
		mainMenuDefaultOrder,
		menuItems,
		parseHomeDataMenuOrderJson,
		partitionMainMenuOrderIndices,
		tabItemsConsumption,
		tabItemsMyHome,
		tabItemsSettings,
		type MenuItemClickDeps,
	} from '../helpers/main_menu';
	import ClassicMainRailMenuEntry from '../components/MainMenu/ClassicMainRailMenuEntry.svelte';
	import { resetSavedScrollPosition } from '../helpers/scroll_position_helpers';
	import { isEmailUnverified } from '../helpers/email_verification_helpers';
	import { isShellyMobileApp } from '../helpers/settings/settings_config_helpers';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';

	// Types
	import type { TypeTabs } from '../types/Tabs';
	// Routes
	import Router, { push, location, replace } from 'svelte-spa-router';

	import DashListsRender from './DashListsRender.svelte';
	import HomeListsRender from './HomeListsRender.svelte';
	import ConsumptionCurrent from '../components/Consumption/ConsumptionCurrent.svelte';
	import ConsumptionOverall from '../components/Consumption/ConsumptionOverall.svelte';
	import UserSettings from './UserSettings.svelte';
	import AppSettings from './AppSettings.svelte';
	import HomeSettings from './HomeSettings.svelte';
	import PowerPricesSettings from './PowerPricesSettings.svelte';
	import NotFound from './NotFound.svelte';
	import AmazonAlexaAppLinking from './AmazonAlexaAppLinking.svelte';
	import CuryIntegration from './CuryIntegration.svelte';
	import ZendureIntegration from './ZendureIntegration.svelte';
	import LoqedIntegration from './LoqedIntegration.svelte';
	import Integrations from './Integrations.svelte';

	// import AppToAppAlexa from './AppToAppAlexa.svelte';
	import PremiumSettings from './PremiumSettings.svelte';

	// Design system components
	import {
		MainLayout,
		Header,
		IconButton,
		MainMenu,
		Tabs,
		Tab,
		Drawer,
		Text,
		LoaderDialog,
	} from '@Shelly/shelly-design-system';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';

	import { showNewsletterModal } from 'src/stores/ui/on-log-modals';
	import { showCalibrationHasStartedModal } from 'src/stores/ui/calibration-modals';
	import AlexaIntegrationPrompt from 'src/components/AlexaIntegrationPrompt.svelte';

	import { globalLoaderMsg, showGlobalLoader } from 'src/stores/ui/global-loader';

	// Components
	import DrawerController from '../components/Drawer/DrawerController.svelte';
	import DrawerStatusBar from '../components/Drawer/DrawerStatusBar.svelte';

	import DashboardEditTabsModal from 'src/components/Lists/Dashboards/DashboardEditTabsModal.svelte';

	import EventLog from '../components/EventLog/EventLog.svelte';
	import EmptyDeviceList from '../components/Lists/EmptyDeviceList.svelte';
	import IncludeAddModals from '../components/IncludeAddModals.svelte';
	import UnlockAuthLockedDevice from '../components/Devices/DeviceWizard/UnlockAuthLockedDevice.svelte';

	import DiscoveredDevicesListHolder from '../components/Lists/Devices/DiscoveredDevicesListHolder.svelte';
	import {
		checkIsNonDashboardTab,
		getIsActivityLogVisible,
		getIsCameraMonitoringVisible,
		isDashTabVisible,
	} from '../components/Lists/list-helpers';
	import NewsletterModal from './NewsletterModal.svelte';

	import BleControlModal from 'src/components/BleControlModal.svelte';

	import { savePrevScrollPosition, setPrevScrollPosition } from 'src/helpers/scroll_position_helpers';

	import { rebootRequired } from 'src/stores/device-runtime/states';

	import RebootPrompt from 'src/components/RebootDialog/RebootPrompt.svelte';
	import DropdownProfile from 'src/components/Profiles/DropdownProfile.svelte';
	import SimpleLogout from 'src/components/Profiles/SimpleLogout.svelte';
	import MonthlyReports from 'src/components/Consumption/MonthlyReports.svelte';
	import EnergyDistribution from 'src/components/Consumption/EnergyDistribution/EnergyDistribution.svelte';
	import PVSetup from 'src/components/Consumption/PVSetupEnergy/PVSetup.svelte';

	import compareVersions from 'compare-versions';
	import { includeDeviceModal } from 'src/stores/features/inclusion';
	import { force_go_back } from 'src/services/native_container_requests_android';
	import TabRightSlotContent from 'src/components/TabRightSlotContent.svelte';

	import { isDevServerUser, isT1User } from 'src/helpers/user_server_conditions';

	import Discounts from './Discounts.svelte';
	import PromoDropdownWithProactiveModals from 'src/components/PromoDropdownWithProactiveModals.svelte';
	import CalibrationHasStartedModal from './CalibrationHasStartedModal.svelte';
	import ModalsContainer from 'src/components/Modals/ModalsContainer.svelte';
	import { isSubAccount, isSubUnit } from 'src/helpers/user_helpers';
	import { isExperimentalEnabled } from 'src/helpers/user_settings_helpers';
	import { NativeOperationSystem } from 'src/services/init/types/init-type';

	import CameraMonitoringRoute from 'src/components/Camera/CameraMonitoringRoute.svelte';
	// AI Chatbot
	import AIChatModal from 'src/components/AISceneChatbot/AIChatModal.svelte';
	import AIChatOnboardingModal from 'src/components/AISceneChatbot/AIChatOnboardingModal.svelte';
	import AIChatPrivacyPolicyModal from 'src/components/AISceneChatbot/AIChatPrivacyPolicyModal.svelte';
	import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
	import { save_profile } from 'src/helpers/profile_helpers';
	import { drawerState, drawerRef } from 'src/stores/ui/sidepanel-public';
	import AiBreakdown from 'src/components/Consumption/AiBreakdown/components/AiBreakdown.svelte';
	import { bridgeTouchCancelToTouchEnd } from 'src/components/Sidepanel/sidepanel-helpers';

	const isApp = $app;
	const mainRoutes = {
		'/': DashListsRender,
		'/dash/event_log': EventLog, // keep up in the list to override the wild card params
		'/dash/camera_monitoring': CameraMonitoringRoute, // Camera monitoring dashboard
		'/dash/:id': DashListsRender,
		'/dash/:type/:id/': DashListsRender,

		'/home/discovered': DiscoveredDevicesListHolder,
		'/home/:type/:id/': HomeListsRender,
		'/home/:type/:id/:room_list': HomeListsRender,

		'/cons/-1': ConsumptionCurrent,
		'/cons/0': ConsumptionOverall,
		'/cons/2': MonthlyReports,
		'/cons/3': EnergyDistribution,
		'/cons/6': AiBreakdown,

		'/settings/user': UserSettings,
		'/settings/user/*': UserSettings,
		'/settings/app': AppSettings,
		'/settings/house': HomeSettings,
		'/settings/house/*': HomeSettings,
		'/cons/power_prices': PowerPricesSettings,
		'/cons/pv_set_up': PVSetup,
		'/settings/app-to-app-alexa': AmazonAlexaAppLinking,
		'/settings/integrations/app-to-app-cury': CuryIntegration,
		'/settings/integrations/zendure': ZendureIntegration,
		'/settings/integrations/loqed': LoqedIntegration,
		'/settings/integrations': Integrations,
		'/settings/premium': PremiumSettings,
		'/settings/discounts': Discounts,
		'*': NotFound,
	};

	let dashboardsEdit = false;
	let emptyDeviceListShow = false;
	let showSharedManagedModal = false;
	let activeMenuItem = 0;
	let activeTabIndex = 0;
	let currentTabItems: TypeTabs = [];
	let menuTabsById: Record<string, TypeTabs> = {};
	let drawerHTMLRef: HTMLElement;
	let mainLayoutScrollRef: HTMLDivElement;
	let routeScrollRef: HTMLDivElement;
	let accountSwitchingEnabled = false;
	let windowClientWidth: number;

	$: $is_mobile = windowClientWidth <= SystemScreenBreakpoints.MOBILE_MAX;

	$: locationSplit = $location.split('/').filter(Boolean);
	$: routeParams = {
		id: locationSplit[1] === 'room' ? locationSplit[2] : '-1',
		type: locationSplit[1],
	};
	$: ($location, savePrevScrollPosition(mainLayoutScrollRef));
	$: ($location, resetFiltersAllDevicesList());
	$: isActivityLogVisible = getIsActivityLogVisible($profile_data);
	$: isCameraMonitoringVisible = getIsCameraMonitoringVisible($profile_data);

	// Guarantee every classic rail item (settings, assistant, add-device, ...) is
	// present even if the persisted `menuOrder` predates it; missing indices are
	// appended at the end without reordering existing ones.
	const normalizePersistedClassicMenuOrder = (parsed: number[]) => {
		let order = appendMissingMenuOrderIndices(parsed, menuItems);

		if (isSubUnit()) {
			order = order.filter((item) => item !== MAIN_MENU_ORDER_INDEX_ASSISTANT);
		}
		return order;
	};

	$: menuOrder = normalizePersistedClassicMenuOrder(
		parseHomeDataMenuOrderJson($profile_data?.menuOrder),
	);

	const handleMenuRoutes = (route = '') => {
		const menuOrder = normalizePersistedClassicMenuOrder(
			parseHomeDataMenuOrderJson($profile_data?.menuOrder),
		);

		if (JSON.stringify(mainMenuDefaultOrder) !== JSON.stringify(menuOrder)) {
			activeMenuItem = menuOrder[0];
		}

		const currentHref = activeMenuItem > 0 ? menuItems[activeMenuItem].href : menuItems[0].href;
		const currentLocation = Object.keys(mainRoutes).find((key) => key.includes(currentHref));

		if (route.includes('integrations/app-to-app-alexa')) {
			replace('/settings/app-to-app-alexa');
		}

		if ($location === '/' || route === '/') {
			if (currentHref === '/include_devices' || currentHref === '/dash') {
				let routeTo = '-1';

				if ($dashboardTabs.length > 2) {
					routeTo = $dashboardTabs.filter((tab) => tab.position == 0)[0]?.id || routeTo;
				}

				replace(`/dash/${routeTo}`);
			} else if (currentHref === '/home') {
				replace(`/home/rooms/-1`);
			} else {
				mainRoutes['/'] = mainRoutes[currentLocation];
				push(currentLocation);
			}
		}

		return mainRoutes;
	};

	const routeLoading = (e) => {
		if (e.detail.location.includes('device') && !e.detail.location.includes('home')) {
			const device = $devices[e.detail.location.split('/')[2]];

			if (device.room_id < 0) {
				replace(`/home/devices/-1`);
			} else {
				replace(`/home/room/${device.room_id}/devices`);
			}

			drawerState.set({ ...$drawerState, type: 'device', id: device.id });
			$drawerRef.open();
		}

		handleMenuRoutes(e.detail.location);
	};

	const routeLoaded = () => {
		if (!$location.includes('rooms') && !$location.includes('room') && !$location.includes('dash')) {
			return;
		}

		setPrevScrollPosition(mainLayoutScrollRef);
	};

	const getActiveIcon = (icon: string) => {
		const parts = String(icon).trim().split(/\s+/).filter(Boolean);
		const iconClass = parts.length >= 2 ? parts[1] : (parts[0] ?? '');
		return `fas ${iconClass}`;
	};
	const isAssistantMenuItem = (item: (typeof menuItems)[number]) => {
		return item.label === 'label_menu_items_assistant';
	};

	const shouldRenderMenuItem = (item: (typeof menuItems)[number]) =>
		!(item.hideForSub && isSubAccount());

	$: mainMenuOrderPartition = partitionMainMenuOrderIndices(menuOrder, menuItems);

	$: visibleClassicFooterMenuIndices = mainMenuOrderPartition.footer.filter((itemIndex) => {
		const item = menuItems[itemIndex];
		return shouldRenderMenuItem(item);
	});

	// Derived from `$location` (not `activeMenuItem`) — `activeMenuItem` is set
	// in `beforeUpdate`, which runs after `$:` blocks in the same flush.
	$: activeMenuItemDef = getHeaderMenuItemDef($location);

	$: if (menuOrder.length > menuItems.length) {
		menuOrder = menuOrder.filter((item) => item !== MAIN_MENU_ORDER_INDEX_ADD_DEVICE);
	}

	const resetFilters = () => {
		deviceMainFilter.set('');
	};

	const menuClickDeps: MenuItemClickDeps = {
		isApp,
		openAddDevice: () => includeDeviceModal.update((s) => ({ ...s, show: true })),
		setEmptyDeviceListShow: (show) => {
			emptyDeviceListShow = show;
		},
		openAiChat: () => aiSceneChatStore.openModal(),
		trackAiFirstSeen: () => trackAiMenuItemFirstSeen(),
	};

	const handlerClickMenuItem = (index: number) => {
		const item = menuItems[index];

		if (item.id === MENU_ITEMS_IDS.addDevice && isActiveHomeShared()) {
			showSharedManagedModal = true;
			return;
		}

		if (item.createOnClick) {
			item.createOnClick(menuClickDeps)();
			return;
		}

		if (isAssistantMenuItem(item)) {
			aiSceneChatStore.openModal();
			trackAiMenuItemFirstSeen();
			return;
		}

		activeMenuItem = index;
		const href = item.href;

		drawerState.set({
			...$drawerState,
			type: href === '/home' ? 'rooms' : 'dashboard',
			id: '-1',
		});

		const tabs = menuTabsById[item.id] || [];
		const tabIndex = href === '/dash' ? tabs.findIndex((tab) => !checkIsNonDashboardTab(tab)) : 0;

		push(href + (tabs[tabIndex]?.route ?? ''));
		resetFilters();
		resetSavedScrollPosition(mainLayoutScrollRef);
	};

	function trackAiMenuItemFirstSeen() {
		if ($profile_data?.aiMenuItemSeen) return;
		const dateNow = new Date();
		save_profile('aiMenuItemSeen', { seen: true, date: dateNow });
		profile_data.set({ ...$profile_data, aiMenuItemSeen: { seen: true, date: dateNow } });
	}

	const handlerClickTabItem = (index: number) => {
		activeTabIndex = index;
		drawerState.set({
			...$drawerState,
			type: currentTabItems[index].key,
			id: '-1',
		});

		const settingsLocation = $location.indexOf('/settings') > -1;
		const menuItemIndex =
			typeof activeMenuItem === 'number' &&
			activeMenuItem >= 0 &&
			activeMenuItem < menuItems.length
				? activeMenuItem
				: 0;

		if (!settingsLocation && menuItemIndex !== activeMenuItem) {
			activeMenuItem = menuItemIndex;
		}

		const pathPrefix = settingsLocation ? '/settings' : menuItems[menuItemIndex].href;
		push(pathPrefix + currentTabItems[index].route);
		resetFilters();
		resetSavedScrollPosition(mainLayoutScrollRef);
	};

	const setNativeBackAndForwardNavigation = (enable: boolean) => {
		if (!isApp) {
			return;
		}

		if ($os === NativeOperationSystem.IOS) {
			backForwardNavigation(enable);
			return;
		}

		// For android it will be the otherway around
		force_go_back(!enable);
	};

	const updateDrawerStateClose = () => {
		if (!$drawerState.open) {
			drawerState.set({
				...$drawerState,
				open: false,
				// set drawer default state
				id: routeParams.id,
				type: routeParams.type,
			});
		}

		// enable iOS native back/forward navigation when the drawer closes
		setNativeBackAndForwardNavigation(true);

		return drawerHTMLRef.removeEventListener('transitionend', updateDrawerStateClose);
	};

	const onOpenDrawer = () => {
		drawerState.set({ ...$drawerState, open: true });
		// disable iOS native back/forward navigation while the drawer is open
		setNativeBackAndForwardNavigation(false);
	};

	const onCloseDrawer = () => {
		// Used event listener to prevent using timeout
		drawerHTMLRef.addEventListener('transitionend', updateDrawerStateClose);

		// REMOVE FOCUSED ELEMENT - clicked before AdvancedCard,ListMenuCard ...
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
	};

	const handleKeydown = (e: KeyboardEvent) => {
		const openModalsLength = document.querySelectorAll('.modal-container').length;

		if (e.key === 'Escape' && $drawerState.open && openModalsLength <= 0) {
			$drawerRef.close();
		}
	};

	const addDiscoveredTab = () => {
		tabItemsMyHome.update((arr) => {
			if (arr.find((tab) => tab.key === 'discovered')) return arr;
			return [
				...arr,
				{
					id: arr.length,
					key: 'discovered',
					text: 'tab_discovered',
					defaultText: 'discovered',
					route: '/discovered',
				},
			];
		});
	};

	const setAccountSwitching = () => {
		const isIOS = $os === 'IOS';
		const version = isIOS ? $ios_version : $android_version;
		const minVersion = isIOS ? '1.1.1' : '1.16.1';

		try {
			if (compareVersions.compare(version, minVersion, '>=')) {
				accountSwitchingEnabled = true;
			}
		} catch (error) {
			console.error('we could not compare versions');
		}
	};

	function setSettingsTabsVisibility() {
		let settingsTabs = tabItemsSettings;

		if (!isShellyMobileApp()) {
			settingsTabs = settingsTabs.filter((tab) => tab.key !== 'app_settings');
		}

		if ($user_data.installer) {
			settingsTabs = settingsTabs.filter((tab) => tab.key !== 'premium');
		}

		return settingsTabs;
	}

	let teardownTouchCancelBridge: (() => void) | undefined;

	// handle global modals escape key
	onMount(() => {
		document.addEventListener('keydown', handleKeydown);
		teardownTouchCancelBridge = bridgeTouchCancelToTouchEnd();

		if (!isApp) {
			accountSwitchingEnabled = true;
			return;
		}

		addDiscoveredTab();
		setAccountSwitching();
	});

	beforeUpdate(() => {
		const _tabItemsConsumption = tabItemsConsumption.slice();
		if (isExperimentalEnabled('pvs')) {
			_tabItemsConsumption.push({
				id: 5,
				key: 'pv_set_up',
				text: 'tab_pv_set_up',
				defaultText: 'PV set-up',
				route: '/pv_set_up',
				labs: true,
			});
		}

		// App Settings is only available inside the Shelly mobile app (per FigJam);
		// hide its tab on web/desktop (incl. browser responsive mode).
		let settingsTabs = setSettingsTabsVisibility();

		menuTabsById = {
			[MENU_ITEMS_IDS.dash]: $dashboardTabs,
			[MENU_ITEMS_IDS.home]: $tabItemsMyHome,
			[MENU_ITEMS_IDS.energy]: _tabItemsConsumption,
			[MENU_ITEMS_IDS.settings]: settingsTabs,
			[MENU_ITEMS_IDS.assistant]: [],
			[MENU_ITEMS_IDS.addDevice]: [],
		};

		const testUser = isDevServerUser(['repo', 'nb']) || isT1User() || import.meta.env.DEV;
		Object.keys(menuTabsById).forEach((id) => {
			menuTabsById[id] = menuTabsById[id].filter((i) => !i.testUser || testUser);
		});
		settingsTabs = settingsTabs.filter((i) => !i.testUser || testUser);

		const locationActiveMenuItem = menuItems.findIndex((item) => {
			if (isAssistantMenuItem(item)) return false;
			return $location.indexOf(item.href) > -1;
		});
		activeMenuItem = locationActiveMenuItem;

		const isSettingsLocation = $location.indexOf('/settings') > -1;
		if (isSettingsLocation) {
			currentTabItems = settingsTabs;
		} else {
			const activeMenuItemObj = activeMenuItem > -1 ? menuItems[activeMenuItem] : undefined;
			const fallbackId = menuItems[0]?.id;
			currentTabItems =
				(activeMenuItemObj?.id && menuTabsById[activeMenuItemObj.id]) ||
				(fallbackId ? menuTabsById[fallbackId] : []) ||
				[];
		}

		//get the active tab - get the default route from menuItemsTabs
		// Note(aav): I have no idea what I am reading. Too scared to touch it.
		// I will come back to it if I gather enough courage.
		activeTabIndex = currentTabItems.findIndex((item) => {
			if (isSettingsLocation) {
				if ($location.match('amazon')) {
					return (
						trimLastChar(item.route.split('/')[1]).split('?')[0] === $location.split('/')[2]
					);
				}
				if ($location.match('user')) {
					return item.route.split('/')[1].split('?')[0] === $location.split('/')[2];
				}
				if ($location.match('integrations/')) {
					return item.route.split('/')[1] === $location.split('/')[2];
				}
				if ($location.match('app-to-app-alexa')) {
					return item.route.split('/')[1] === 'integrations';
				}
				if ($location.match('app-to-app-cury')) {
					return item.route.split('/')[1] === 'integrations';
				}
				return (
					`/settings${item.route}` === $location ||
					trimLastChar(item.route.split('/')[1]) === $location.split('/')[2]
				);
			}
			if (activeMenuItem > -1) {
				if (menuItems[activeMenuItem].href === '/home') {
					return (
						menuItems[activeMenuItem].href +
							item.route.substring(0, item.route.lastIndexOf('/')) ===
							$location.substring(0, $location.lastIndexOf('/')) ||
						trimLastChar(item.route.split('/')[1]) === $location.split('/')[2]
					);
				} else {
					//Bobi: Absolutely stupid fast fix
					//Alex: I will use it! ($location.match(''))
					if ($location.match('amazon')) {
						return (
							trimLastChar(item.route.split('/')[1]).split('?')[0] ===
							$location.split('/')[2]
						);
					} else if ($location.match('user')) {
						return item.route.split('/')[1].split('?')[0] === $location.split('/')[2];
						// Keep Integrations tab active when in integrations subroutes
					} else if ($location.match('integrations/')) {
						return item.route.split('/')[1] === $location.split('/')[2];
						// Keep Integrations tab active when in Alexa tab
						// It does not have 'integrations' in the route and we are selecting it manually
					} else if ($location.match('app-to-app-alexa')) {
						return item.route.split('/')[1] === 'integrations';
					} else if ($location.match('app-to-app-cury')) {
						return item.route.split('/')[1] === 'integrations';
					} else {
						return (
							menuItems[activeMenuItem].href + item.route === $location ||
							trimLastChar(item.route.split('/')[1]) === $location.split('/')[2]
						);
					}
				}
			} else {
				return 0;
			}
		});
	});

	onDestroy(() => {
		document.removeEventListener('keydown', handleKeydown);
		teardownTouchCancelBridge?.();
	});
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

<!-- Version and tags in the app header -->
<div class="d-flex" style="position: absolute; top: 4px; right: 4px; z-index: 100; ">
	<Text styleType="miniature" class="text-primary mr-xs">
		{import.meta.env.MODE !== 'production' ? import.meta.env.MODE : ''}
	</Text>
	<Text styleType="miniature">
		{version}
	</Text>
</div>

<!-- Main Container App Layout -->
<MainLayout
	backgroundImgSrc={getAppBackgroundSrc($profile_data, true)}
	backgroundColor={$profile_data?.layout?.backgroundColor || undefined}
	bind:scrollRef={mainLayoutScrollRef}
	isMobileApp={false}
>
	<Header slot="header">
		<div slot="leading" class="flex-1 min-width-0">
			<div class="d-flex align-items-center min-width-0 width-full">
				<button
					type="button"
					class="header-shelly-logo-btn flex-shrink-0"
					aria-label={$format('button_go_to_shelly', {
						default: 'Button go to Shelly',
					})}
					on:click={() => push(getLandingHref())}
				>
					<img src="images/shelly-logo.svg" alt="" class="header-shelly-logo" />
				</button>
			</div>
		</div>

		<PromoDropdownWithProactiveModals />

		<div class="mr-s" />

		{#if !accountSwitchingEnabled}
			<SimpleLogout />
		{:else}
			<DropdownProfile scrollMaxHeight={null} isPremium={$user_data.premium ? true : false} />
		{/if}
	</Header>

	<MainMenu slot="mainMenu">
		<div class="classic-main-menu-dock">
			<div class="classic-main-menu-dock__primary">
				{#each mainMenuOrderPartition.primary as itemIndex (itemIndex)}
					<ClassicMainRailMenuEntry
						{menuItems}
						{itemIndex}
						{activeMenuItem}
						{getActiveIcon}
						{shouldRenderMenuItem}
						onMenuItemClick={handlerClickMenuItem}
					/>
				{/each}
			</div>
			{#if visibleClassicFooterMenuIndices.length}
				<div class="classic-main-menu-dock__grow" aria-hidden="true"></div>
				<div class="classic-main-menu-dock__footer">
					{#each visibleClassicFooterMenuIndices as itemIndex (itemIndex)}
						<ClassicMainRailMenuEntry
							{menuItems}
							{itemIndex}
							{activeMenuItem}
							{getActiveIcon}
							{shouldRenderMenuItem}
							onMenuItemClick={handlerClickMenuItem}
						/>
					{/each}
				</div>
			{/if}
		</div>
	</MainMenu>

	<svelte:fragment slot="staticTop">
		<Tabs class="mb-m" location={activeTabIndex} hideSlotAction={$location.indexOf('dash') === -1}>
			<svelte:fragment slot="action">
				{#if $location.indexOf('dash') !== -1}
					<IconButton
						aria-label={$format('button_edit', { default: 'Edit' })}
						icon="fas fa-pen"
						variant="secondary"
						on:click={() => {
							dashboardsEdit = true;
						}}
					/>
				{/if}
			</svelte:fragment>
			{#each currentTabItems as item, index}
				{#if isDashTabVisible(item, isActivityLogVisible, isCameraMonitoringVisible)}
					<Tab
						class={'text-capitalize' + (item.labs ? ' tab-shelly-labs' : '')}
						notification={item.key === 'user_settings' && $isEmailUnverified
							? 'danger'
							: item.notification}
						active={index === activeTabIndex}
						icon={activeMenuItem === 0 ? undefined : item.icon}
						on:click={(event) => {
							event.preventDefault();
							handlerClickTabItem(index);
						}}
						hideRightSlot={!item?.isPremium &&
							!item.circleAvatarIcon &&
							!item.alpha &&
							!item.beta &&
							!item.labs}
					>
						{$format(
							!$showNewLayout &&
								['user_settings', 'app_settings', 'home_settings'].includes(item.key)
								? `${item.text}_short`
								: item.text,
							{ default: item.defaultText },
						)}
						<TabRightSlotContent
							slot="rightSlot"
							{...item.beta
								? {
										labelProps: { size: 'xs', type: 'secondary', value: 'BETA' },
									}
								: {}}
							{...item.alpha
								? {
										labelProps: { size: 'xs', type: 'warning', value: 'alpha' },
									}
								: {}}
							{...item.isPremium
								? {
										circleAvatarProps: { size: 's', icon: item.circleAvatarIcon },
									}
								: {}}
							{...item.labs
								? {
										labelProps: {
											size: 's',
											type: 'primary-10',
											icon: 'fa-solid fa-vial',
											value: $format('label_shelly_labs', {
												default: 'SHELLY LABS',
											}),
										},
									}
								: {}}
						/>
					</Tab>
				{/if}
			{/each}
		</Tabs>
	</svelte:fragment>

	<Router
		slot="scroll"
		routes={handleMenuRoutes()}
		on:routeLoading={routeLoading}
		on:routeLoaded={routeLoaded}
		bind:routeScrollRef
	/>

	<Drawer
		slot="drawerMenu"
		bind:this={$drawerRef}
		bind:drawerHTMLRef
		onOpen={onOpenDrawer}
		onClose={onCloseDrawer}
		hideSlotStatsBar={$drawerState.type !== 'device'}
	>
		<DrawerStatusBar slot="statsBar" />
		<DrawerController />
	</Drawer>
</MainLayout>

<!-- Modals -->
<IncludeAddModals />

{#if !$app}
	<EmptyDeviceList hideMessage bind:showModal={emptyDeviceListShow} />
{/if}

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context="add_device" />

<!-- =auth unlock modal // string/undefined -->
{#if typeof $authLockedDeviceModal !== 'undefined'}
	<UnlockAuthLockedDevice />
{/if}

<NewsletterModal bind:showModal={$showNewsletterModal} />

<!-- Place ModalsContainer after Router but inside app-container -->
<ModalsContainer />

<!-- Calibration Has Started Modal, called by some devices when the device calibration is triggered externally -->
<CalibrationHasStartedModal
	bind:showModal={$showCalibrationHasStartedModal.visible}
	device={$showCalibrationHasStartedModal.device}
/>

<!-- Device reboot prompt, may be called by some devices when the device reboot is triggered externally -->
<RebootPrompt showModal={typeof $rebootRequired !== 'undefined'} />
<AlexaIntegrationPrompt />
<DashboardEditTabsModal bind:dashboardsEdit />

{#if typeof $showBleControlSetupModal !== 'undefined'}
	<BleControlModal />
{/if}

{#if $showGlobalLoader}
	<LoaderDialog
		show={$showGlobalLoader}
		title={$globalLoaderMsg
			? $globalLoaderMsg
			: `${$format('loader_overlay_switching_profile_title', {
					default: 'Switching your profile.',
				})}..`}
	/>
{/if}

<!-- {#if !isSubUnit()} -->
<!-- AI Chat Onboarding Modal (shows before AI chat for first-time users) -->
<AIChatOnboardingModal />

<!-- AI Chat Privacy Policy Modal -->
<AIChatPrivacyPolicyModal />

<!-- AI Scene Chatbot Modal (always rendered; Modal controls visibility via showModal prop) -->
<AIChatModal />

<!-- {/if} -->

<style>
	/* When AI chat modal is open: lock layout scroll and block layout pointer-events (body class set by AISceneModalWrapper scroll lock). */
	:global(body.ai-scene-modal-open) {
		overflow: hidden;
		overscroll-behavior: none;
	}
	:global(body.ai-scene-modal-open .main-layout-scroll),
	:global(body.ai-scene-modal-open .main-layout .scroll-content-new-layout),
	:global(body.ai-scene-modal-open .scroll.scroll-drawer) {
		overflow: hidden !important;
		overscroll-behavior: none;
		touch-action: none;
		height: var(--ios-visual-viewport-height, 100vh);
		max-height: var(--ios-visual-viewport-height, 100vh);
	}
	:global(body.ai-scene-modal-open .main-layout) {
		pointer-events: none;
	}

	:global(.main-layout:not(.new-layout) .inner-container) {
		height: 100%;
	}

	:global(.tab.tab-shelly-labs) {
		max-width: none !important;
	}
	:global(.tab.tab-shelly-labs .right-slot-wrapper .label) {
		max-width: none !important;
	}

	.header-shelly-logo-btn {
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;
		margin-right: 40px;
	}

	.header-shelly-logo {
		height: 28px;
		width: auto;
	}

	@media screen and (max-width: 1123px) {
		.header-shelly-logo-btn {
			margin-left: 0;
			margin-right: var(--spacing-l);
		}
	}

	@media screen and (max-width: 639px) {
		.header-shelly-logo-btn {
			margin-left: 0;
			margin-right: var(--spacing-m);
		}
	}
</style>
